// The world around the race (ui/index.md, "sunset grandstand"): sky and light, turf lanes,
// edging, start stalls, the finish gantry, the grandstand with its crowd, grass, scenery and the
// podium. One `Environment` builds it into a scene and is updated once per frame.
// The sky, light and season follow the real time in Joutseno (almanac.ts, daylight.ts,
// calendar.ts): day, sunset, blue hour and floodlit night; snow, autumn birches, lake ice; and
// holiday themes such as Christmas and Halloween (seasonal.ts).
//
// Hooks for other jobs:
// - `lanes[i].uniforms.tripIntensity` / `sky.uniforms.uTripIntensity`: the catnip trip.
// - `anchors`: where the director finds the stalls, the finish, the podium and the crowd.
// - `setFocus(x)`: keep sun shadows and the 3D crowd around the camera's subject (defaults to the pack).
import { Group, Vector3, type Camera, type Scene, type WebGLRenderer } from 'three';
import { LANE_COUNT, TRACK_LENGTH, laneCenterZ, posToX } from '../../data/track';
import type { RaceEvent, RaceSnapshot } from '../../race/types';
import type { AssetStage, AssetStore } from '../assets';
import type { TierSettings } from '../quality';
import { Crowd } from './crowd';
import { Gantry } from './gantry';
import { Gates } from './gates';
import { GradeEffect } from './grade';
import { Grandstand } from './grandstand';
import { Grass } from './grass';
import { Lanes, type LaneUniforms } from './lanes';
import { joutsenoTime, moonPosition, skyDirection, sunPosition, type LocalTime } from './almanac';
import { seasonOn, themesOn, type SeasonState, type ThemeId } from './calendar';
import { daylight, lightName, type Daylight, type Rgb } from './daylight';
import { Floodlights } from './floodlights';
import { GANTRY, PODIUM, STAND, rowSeat } from './layout';
import { Lighting } from './lighting';
import { SEASON_UNIFORMS } from './seasonUniforms';
import { Seasonal, themeColours } from './seasonal';
import { FUR_RIM } from '../cats/fur';
import { createMaterials, environmentAssetEntries } from './materials';
import { Podium } from './podium';
import { Props } from './props';
import { Rails } from './rails';
import { Sky } from './sky';
import { TrackMarkings } from './track';

/** The time, light and season in Joutseno as the stadium shows them, for tests and the debug overlay. */
export interface SkyInfo {
  /** Joutseno local time, `YYYY-MM-DD HH:MM`. */
  local: string;
  sunElevation: number; sunAzimuth: number; moonElevation: number;
  light: 'day' | 'golden' | 'twilight' | 'night';
  /** Floodlights, 0..1. */
  floodlights: number;
  snow: number; leaves: number; lakeIce: number; snowfall: number; leafFall: number;
  themes: ThemeId[];
  /** Holiday pieces and particle layers on show. */
  decorations: string[];
}

const smoothstep = (e0: number, e1: number, x: number) => { const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };
const pad = (n: number) => String(n).padStart(2, '0');

/** The environment's textures; load before building (about 1 MB). */
export function environmentStage(): AssetStage {
  return { name: 'stadium', entries: environmentAssetEntries() };
}

export interface EnvironmentAnchors {
  /** Centre of the start line, in front of the stalls. */
  gate: Vector3;
  /** Centre of the finish line. */
  finish: Vector3;
  /** Top of the finish banner. */
  gantryTop: Vector3;
  /** Podium centre, the three standing spots (1st, 2nd, 3rd) and the way it faces. */
  podium: { center: Vector3; spots: Vector3[]; facing: Vector3 };
  /** A point in the middle of the grandstand's front rows, for crowd shots. */
  crowd: (x: number) => Vector3;
  /** z of each lane's centre line, in lane order. */
  laneZ: number[];
}

export class Environment {
  readonly root = new Group();
  readonly sky: Sky;
  readonly lighting: Lighting;
  /** Golden-hour colour grade; add it to the post chain with `post.addEffect(environment.grade)`. */
  readonly grade = new GradeEffect();
  readonly anchors: EnvironmentAnchors;
  private turf: Lanes;
  private gates: Gates;
  private stand: Grandstand;
  private crowd: Crowd;
  private grass: Grass;
  private props: Props;
  private focusOverride: number | null = null;
  private focusX = 0;
  private phase: RaceSnapshot['phase'] | null = null;
  private podium: Podium;
  private floodlights = new Floodlights();
  private seasonal = new Seasonal();
  /** Direction towards the true sun and moon (the foliage shader reads `sunDir` by reference). */
  private sunDir = new Vector3(0, 0.2, 1);
  private moonDir = new Vector3(0, 1, 0);
  private skyAt = -Infinity;
  private baked: { elevation: number; snow: number } | null = null;
  private themesKey = '';
  private state: { local: LocalTime; sun: { elevation: number; azimuth: number }; moonElevation: number; light: Daylight; season: SeasonState; themes: ThemeId[] } | null = null;

  /** `clock` gives the current time in ms (the device clock, or a fixed start for `?time=`). */
  constructor(private renderer: WebGLRenderer, private scene: Scene, store: AssetStore, tier: TierSettings,
    private clock: () => number = () => Date.now()) {
    this.root.name = 'environment';
    this.sky = new Sky();
    scene.add(this.sky.mesh);
    scene.background = null;
    this.lighting = new Lighting(scene, tier);

    const mats = createMaterials(store);
    this.turf = new Lanes();
    this.gates = new Gates(mats);
    this.stand = new Grandstand(mats);
    this.crowd = new Crowd(this.stand.modulesX, tier.crowd, (from, to) => this.stand.setLiveCrowdModules(from, to));
    this.grass = new Grass(tier.grassBlades);
    this.props = new Props(this.sunDir);
    const podium = new Podium(mats);
    this.podium = podium;
    this.root.add(
      this.turf.group, new TrackMarkings().group, new Rails(mats).group, this.gates.group, new Gantry(mats).group,
      this.stand.group, this.crowd.group, this.grass.mesh, this.props.group, podium.group,
      this.floodlights.group, this.seasonal.group,
    );
    scene.add(this.root);
    this.updateSky(true);

    this.anchors = {
      gate: new Vector3(0, 0, 0),
      finish: new Vector3(TRACK_LENGTH, 0, 0),
      gantryTop: new Vector3(GANTRY.x, GANTRY.beamY + 1.3, 0),
      podium: { center: new Vector3(PODIUM.x, 0, PODIUM.z), spots: podium.spots, facing: podium.facing },
      crowd: (x: number) => { const s = rowSeat(Math.floor(STAND.rows / 2)); return new Vector3(x, s.y + 0.4, s.z); },
      laneZ: Array.from({ length: LANE_COUNT }, (_, i) => laneCenterZ(i)),
    };
  }

  /** Per-lane trip uniforms, in lane order. */
  get laneUniforms(): LaneUniforms[] { return this.turf.lanes.map((l) => l.uniforms); }

  /** Pins the shadow box and 3D crowd to a world x; null returns to following the pack. */
  setFocus(x: number | null): void { this.focusOverride = x; }

  applyTier(t: TierSettings): void {
    this.lighting.applyTier(t);
    this.crowd.setCount(t.crowd);
    this.grass.setCount(t.grassBlades);
  }

  update(snap: RaceSnapshot, events: RaceEvent[], dt: number, timeS: number, camera: Camera): void {
    // Follow the pack: the middle of the leading group, or the podium while results show.
    let lead = 0, sum = 0;
    for (const c of snap.cats) { const x = posToX(c.displayPos); lead = Math.max(lead, x); sum += x; }
    const mean = snap.cats.length ? sum / snap.cats.length : 0;
    const packX = (lead + mean) / 2;
    const target = this.focusOverride ?? (snap.phase === 'finished' ? Math.min(lead, TRACK_LENGTH + 10) : packX);
    this.focusX += (target - this.focusX) * (1 - Math.exp(-dt * 4));
    if (Math.abs(target - this.focusX) > 80) this.focusX = target; // a jump (new race, seek)

    if (snap.phase !== this.phase) { this.phase = snap.phase; this.gates.setPhase(snap.phase); }
    for (const e of events) if (e.type === 'finish' && e.place === 1) this.crowd.cheer(1);

    const wall = performance.now();
    if (wall - this.skyAt >= 1000) this.updateSky(false);
    this.seasonal.update({
      timeS, camera, viewportHeight: this.renderer.domElement.height, night: this.state!.light.flood,
      light: particleLight(this.state!.light), hour: this.state!.local.hour,
    });
    this.lighting.setFocus(this.focusX);
    this.lighting.update();
    this.sky.update(timeS, camera.position);
    this.turf.update(timeS);
    this.gates.update(dt);
    this.stand.update(timeS);
    this.crowd.update(dt, timeS, this.focusX, packX, snap.phase, snap.phaseElapsedMs);
    this.grass.update(timeS, camera);
    this.props.update(timeS, camera);
  }

  /** The time, light and season shown now. */
  skyInfo(): SkyInfo {
    const st = this.state!;
    const l = st.local;
    return {
      local: `${l.year}-${pad(l.month)}-${pad(l.day)} ${pad(l.hour)}:${pad(l.minute)}`,
      sunElevation: st.sun.elevation, sunAzimuth: st.sun.azimuth, moonElevation: st.moonElevation,
      light: lightName(st.sun.elevation), floodlights: st.light.flood,
      snow: st.season.snow, leaves: st.season.leaves, lakeIce: st.season.lakeIce,
      snowfall: st.season.snowfall, leafFall: st.season.leafFall,
      themes: st.themes, decorations: this.seasonal.pieces,
    };
  }

  /** Follows the clock: sun, moon, light, season and themes. Rebakes the sky lighting when it has changed enough. */
  private updateSky(force: boolean): void {
    this.skyAt = performance.now();
    const ms = this.clock();
    const local = joutsenoTime(ms);
    const sun = sunPosition(ms), moon = moonPosition(ms);
    const light = daylight(sun.elevation);
    const season = seasonOn(local.year, local.month, local.day);
    const themes = themesOn(local.year, local.month, local.day, local.hour);
    this.state = { local, sun, moonElevation: moon.elevation, light, season, themes };

    this.sunDir.set(...skyDirection(sun.elevation, sun.azimuth));
    this.moonDir.set(...skyDirection(moon.elevation, moon.azimuth));
    this.sky.set(light.sky, this.sunDir, this.moonDir, light.stars, smoothstep(-1, 3, moon.elevation));
    const flood = light.flood;
    const snowGround: Rgb = [0.45, 0.48, 0.52], grassGround: Rgb = [0.047, 0.069, 0.013];
    const hemiGround = grassGround.map((g, i) => g + (snowGround[i]! - g) * season.snow * 0.6) as Rgb;
    this.lighting.set(light, this.sunDir, hemiGround);
    this.scene.environmentIntensity = light.envIntensity;
    this.grade.setMood(light.grade);
    FUR_RIM.set(...light.rim);

    const sunlit = smoothstep(-2, 4, sun.elevation);
    const u = SEASON_UNIFORMS;
    u.uSnow.value = season.snow; u.uDryGrass.value = season.dryGrass; u.uLeaves.value = season.leaves;
    u.uAutumn.value = season.autumn; u.uSpring.value = season.spring; u.uSunGlow.value = sunlit; u.uFlood.value = flood;
    this.props.setSeason(season, sunlit, flood);
    this.floodlights.set(flood);
    this.stand.setFlowers(season.flowers);
    this.podium.setFlowers(season.flowers);
    this.seasonal.setSeason(season);
    const key = themes.join();
    if (force || key !== this.themesKey) {
      this.themesKey = key;
      this.seasonal.setThemes(themes);
      const colours = themeColours(themes);
      this.props.setFlagColours(colours.flags);
      this.stand.setBulbColours(colours.bulbs);
    }

    const b = this.baked;
    if (force || !b || Math.abs(b.elevation - sun.elevation) > 0.75 || Math.abs(b.snow - season.snow) > 0.05) {
      // Light bouncing up from the ground: grass or snow, lit by the sun or the floodlights.
      const level = Math.min(1.2, (light.sunIntensity + 1.7 * flood) / 4.2);
      const albedo: Rgb = season.snow > 0 ? [0.16 + 0.44 * season.snow, 0.2 + 0.42 * season.snow, 0.07 + 0.59 * season.snow] : [0.16, 0.2, 0.07];
      this.scene.environment = this.sky.bakeEnvironment(this.renderer, albedo.map((a) => a * level) as Rgb);
      this.baked = { elevation: sun.elevation, snow: season.snow };
    }
  }
}

/**
 * The light falling on lit particles (snow, leaves, confetti): sun, floodlights and the sky
 * around them, so falling snow is about as bright as the sky behind it rather than darker.
 */
function particleLight(l: Daylight): Rgb {
  const s = l.sky;
  return [0, 1, 2].map((i) => l.sunColour[i]! * l.sunIntensity * 0.18 + [0.9, 0.94, 1][i]! * l.flood * 0.5
    + (s.upperAway[i]! + s.upperSun[i]! + s.horizonAway[i]! + s.horizonSun[i]!) * 0.15) as Rgb;
}
